import { useContext, /**useRef*/ } from "react";
import { useValidators } from "@/shared/utils/validate";
import { ErrorDialogContext, /**errorHandledAsync,*/ errorHandledSync, } from "@/shared/context/error-context";
import { useSignUpStates } from "@/features/user/hooks/use-sign-up-states";
import { MoneyLinkException } from "@/shared/utils/custom-exception";
import { useNavigate } from "react-router-dom";
import { utilities } from "@/shared/utils/utility";
import { EndpointPathNames } from "@/shared/config/endpoint-path";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";

export const useSignUpPass = () => {
  const navigate = useNavigate();

  const {
    password, // パスワード
    setPassword,
    passwordError, // パスワードのエラーメッセージ
    setPasswordError,
    passwordConfirm,
    setPasswordConfirm,
    isActiveNextButton, // 次へボタンの有効フラグ
    setIsActiveNextButton,
    isShowErrorDialog, // エラーダイアログ表示フラグ情報
    setIsShowErrorDialog,
  } = useSignUpStates();

  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);

  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);

  // バリデーション関数の呼び出し
  const { validatesForm } = utilities();
  const { passwordValidator, isHex32 } = useValidators();

  // Stateが更新されたときのコールバック ==========================
  // パスワード入力情報が更新された場合のコールバック関数定義
  const changeNextButtonState = () => {
    setIsActiveNextButton(password !== "" && passwordConfirm !== "");
  };

  // パスワードの変更
  const onChangePassword = (newValue: string) => { setPassword(newValue);};

  // パスワード（確認用）の変更
  const onChangePasswordConfirm = (newValue: string) => { setPasswordConfirm(newValue);};

  // ユーザー名登録画面へ遷移
  const navigateSignUpUser = (token: string, password: string) => {
    navigate(EndpointPathNames.SIGN_UP_USER, { state: { token: token, password: password } });
  };

  // ボタン押下イベント ======================================
  // 「次へ」押下時のコールバック
  const onClickNext = async (token: string, password: string, passwordConfirm: string) => {
    loadingCtx.setIsLoading(true);

    try {
      let hasValidationError = validatesForm([passwordValidator], password, setPasswordError);
      hasValidationError = validatesForm([passwordValidator], passwordConfirm, setPasswordError);
      if (!hasValidationError) {
        return;
      }
      if (password !== passwordConfirm) {
        setPasswordError("パスワードと確認用パスワードが一致しません。");
        return;
      }

      // tokenのチェック
      const tokenValidation = isHex32(token)
      if (!tokenValidation) {
        navigate(EndpointPathNames.ERROR, { replace: true });
        return;
      }

      // ユーザー名登録画面へ遷移
      navigateSignUpUser(token, password);
    } catch (error) {
      if (error instanceof MoneyLinkException) {
        const { messageBody } = error.error.props;
        // エラーページ遷移の際にメッセージを渡す
        navigate(EndpointPathNames.ERROR, { replace: true, state: messageBody ?? "" });
      } else {
        throw MoneyLinkException.wrapCallback(error, () => { /**intentionally empty */ });
      }
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // エラーダイアログを閉じる
  const closeErrorDialog = () => {
    setIsShowErrorDialog({
      isError: false,
      errorMessage: "",
    });
  };

  return {
    password,
    passwordConfirm,
    passwordError,
    isActiveNextButton,
    isShowErrorDialog,
    onChangePassword: errorHandledSync(onChangePassword, setError),
    onChangePasswordConfirm: errorHandledSync(onChangePasswordConfirm, setError),
    onClickNext: errorHandledSync(onClickNext, setError),
    closeErrorDialog: errorHandledSync(closeErrorDialog, setError),
    changeNextButtonState: errorHandledSync(changeNextButtonState, setError),
  };
};
